// =====================================================================
// calculos.js
// Una función por hoja del Excel Cotizador_VV170326.xlsm.
// Cada función replica la fórmula original 1:1 (referencia de celda en
// el comentario). Todas devuelven el valor total en COP (o USD donde
// aplica), o la cadena "REVISE LAS FECHAS" si fin < inicio.
//
// Los valores numéricos (fees, asistencia, tramos de traslados, etc.)
// se leen de parametrosManual.js y son editables por un admin desde
// Configuraciones → Parámetros Cotizador.
// =====================================================================

import {
  mround,
  isOdd,
  diffDias,
  sumarDias,
  conValidacionFechas
} from "./excelFormulas"
import { parametros as P } from "./parametrosManual"

const fee = () => P.general.fee
const redondeo = () => P.general.redondeo

// ---------------------------------------------------------------------
// TC INTERNACIONAL  (hoja "TC INTERNACIONAL", celdas B33 / G28)
// =IF((fin-ini)<0,"REVISE LAS FECHAS",
//   MROUND(vuelos*1.15 + hotel + traslados + 1.4*pax*(fin-ini+1)*trm*1.15, 1000))
// Aplica igual para "Todo Incluido" y "Sólo Desayuno" (misma fórmula).
// ---------------------------------------------------------------------
export function tcInternacional({ vuelos, hotel, traslados, pax, trm, inicio, fin }) {
  return conValidacionFechas(inicio, fin, d =>
    mround(
      vuelos * fee() + hotel + traslados +
      P.internacional.asistenciaUsdDia * pax * (d + 1) * trm * fee(),
      redondeo()
    )
  )
}

// ---------------------------------------------------------------------
// TC NACIONAL  (hoja "TC NACIONAL", celdas B31 / G26)
// =MROUND(vuelos*1.15 + hotel + (fin-ini+1)*1500*pax*1.15, 1000)
// Nota del Excel: si cotizas Amazonas, sumar 80.000 (TI) o 60.000
// (desayuno) a los vuelos para el traslado — se maneja como ajuste
// manual del usuario en el campo vuelos.
// ---------------------------------------------------------------------
export function tcNacional({ vuelos, hotel, pax, inicio, fin }) {
  return conValidacionFechas(inicio, fin, d =>
    mround(
      vuelos * fee() + hotel + (d + 1) * P.nacional.gastoCopPaxDia * pax * fee(),
      redondeo()
    )
  )
}

// ---------------------------------------------------------------------
// TC MEX  (hoja "TC MEX", celda B27)
// Tours por tramos de pax (en USD, multiplicado por TRM):
//   pax<4:100 | pax<5:140 | pax<11:340 | pax<20:400 | pax<31:780 | else 25*pax
// =MROUND(vuelos*1.15 + hotel + tours*1.15
//   + 1.7*pax*(fin-ini+1)*trm*1.15 + 83*pax*trm*1.15, 1000)
// ---------------------------------------------------------------------
function toursMexUSD(pax) {
  const t = P.mex
  if (pax < 4) return t.toursHasta3
  if (pax < 5) return t.tours4
  if (pax < 11) return t.tours5a10
  if (pax < 20) return t.tours11a19
  if (pax < 31) return t.tours20a30
  return t.toursUsdPaxDesde31 * pax
}

export function tcMex({ vuelos, hotel, pax, trm, inicio, fin }) {
  return conValidacionFechas(inicio, fin, d =>
    mround(
      vuelos * fee() +
      hotel +
      toursMexUSD(pax) * trm * fee() +
      P.mex.asistenciaUsdDia * pax * (d + 1) * trm * fee() +
      P.mex.trasladosUsdPax * pax * trm * fee(),
      redondeo()
    )
  )
}

// ---------------------------------------------------------------------
// TC MEX-CUN  (hoja "TC MEX-CUN", celda B31)
// ⚠️ OJO: en esta hoja los tramos de tours (excepto el primero) están
// DIVIDIDOS entre pax en el Excel original:
//   pax<4: 100*trm | pax<5: 140/pax*trm | pax<11: 340/pax*trm
//   pax<20: 400/pax*trm | pax<31: 780/pax*trm | else 25*pax*trm
// Esto difiere de TC MEX (donde NO se divide). Se replica tal cual;
// si es un error del Excel, cambiar toursMexCunUSD por toursMexUSD.
// Traslados CUN por tramos:
//   pax<8: 250000 | pax<15: 500000 | pax<17: 700000 | else 50000*pax
// =MROUND(vuelos*1.15 + hotelMex + tours*1.15
//   + 1.7*pax*(fin-ini+1)*trm*1.15 + hotelCun + trasladosCun*1.15
//   + 83*pax*trm*1.15, 1000)
// ---------------------------------------------------------------------
function toursMexCunUSD(pax) {
  const t = P.mex
  if (pax < 4) return t.toursHasta3
  if (pax < 5) return t.tours4 / pax
  if (pax < 11) return t.tours5a10 / pax
  if (pax < 20) return t.tours11a19 / pax
  if (pax < 31) return t.tours20a30 / pax
  return t.toursUsdPaxDesde31 * pax
}

export function trasladosCunCOP(pax) {
  const t = P.cancun
  if (pax < 8) return t.trasladosHasta7
  if (pax < 15) return t.traslados8a14
  if (pax < 17) return t.traslados15a16
  return t.trasladosCopPaxDesde17 * pax
}

export function tcMexCun({ vuelos, hotelMex, hotelCun, pax, trm, inicio, fin }) {
  return conValidacionFechas(inicio, fin, d =>
    mround(
      vuelos * fee() +
      hotelMex +
      toursMexCunUSD(pax) * trm * fee() +
      P.mex.asistenciaUsdDia * pax * (d + 1) * trm * fee() +
      hotelCun +
      trasladosCunCOP(pax) * fee() +
      P.mex.trasladosUsdPax * pax * trm * fee(),
      redondeo()
    )
  )
}

// ---------------------------------------------------------------------
// TC CUN  (hoja "TC CUN", celda B32)
// =MROUND(vuelos*1.15 + hotel + 1.7*pax*(fin-ini+1)*trm*1.15
//   + trasladosCun(pax)*1.15, 1000)
// ---------------------------------------------------------------------
export function tcCancun({ vuelos, hotel, pax, trm, inicio, fin }) {
  return conValidacionFechas(inicio, fin, d =>
    mround(
      vuelos * fee() +
      hotel +
      P.cancun.asistenciaUsdDia * pax * (d + 1) * trm * fee() +
      trasladosCunCOP(pax) * fee(),
      redondeo()
    )
  )
}

// ---------------------------------------------------------------------
// TC PLAYA DEL CARMEN  (hoja "TC CUN", celda F32)
// Igual que Cancún pero con otros traslados:
//   pax<8: 700000 | pax<15: 1400000 | pax<17: 1250000 | else 80000*pax
// ---------------------------------------------------------------------
function trasladosPdcCOP(pax) {
  const t = P.playaDelCarmen
  if (pax < 8) return t.trasladosHasta7
  if (pax < 15) return t.traslados8a14
  if (pax < 17) return t.traslados15a16
  return t.trasladosCopPaxDesde17 * pax
}

export function tcPlayaDelCarmen({ vuelos, hotel, pax, trm, inicio, fin }) {
  return conValidacionFechas(inicio, fin, d =>
    mround(
      vuelos * fee() +
      hotel +
      P.playaDelCarmen.asistenciaUsdDia * pax * (d + 1) * trm * fee() +
      trasladosPdcCOP(pax) * fee(),
      redondeo()
    )
  )
}

// ---------------------------------------------------------------------
// TC PUJ (Punta Cana)  (hoja "TC PUJ", celda B32)
// Traslados en USD por tramos:
//   pax<5: 20 | pax<7: 30 | pax<13: 40 | else 6*pax
// =MROUND(vuelos*1.15 + hotel + 1.2*pax*(fin-ini+1)*trm*1.15
//   + trasladosUSD*trm*1.15, 1000)
// ---------------------------------------------------------------------
function trasladosPujUSD(pax) {
  const t = P.puntaCana
  if (pax < 5) return t.trasladosHasta4
  if (pax < 7) return t.traslados5a6
  if (pax < 13) return t.traslados7a12
  return t.trasladosUsdPaxDesde13 * pax
}

export function tcPuntaCana({ vuelos, hotel, pax, trm, inicio, fin }) {
  return conValidacionFechas(inicio, fin, d =>
    mround(
      vuelos * fee() +
      hotel +
      P.puntaCana.asistenciaUsdDia * pax * (d + 1) * trm * fee() +
      trasladosPujUSD(pax) * trm * fee(),
      redondeo()
    )
  )
}

// ---------------------------------------------------------------------
// TC CUBA  (hoja "TC CUBA")
// Varadero (B32):   ... + 57*pax*trm*1.15
// Habana (F27):     ... + 65*pax*trm*1.15
// Var+Hav (J26):    vuelos*1.15 + hotelHav + hotelVar
//                   + 1.2*pax*(fin-ini+1)*trm*1.15 + 109*pax*trm*1.15
// ---------------------------------------------------------------------
function cubaBase({ vuelos, hotel, pax, trm, inicio, fin, usdTraslados }) {
  return conValidacionFechas(inicio, fin, d =>
    mround(
      vuelos * fee() +
      hotel +
      P.cuba.asistenciaUsdDia * pax * (d + 1) * trm * fee() +
      usdTraslados * pax * trm * fee(),
      redondeo()
    )
  )
}

export function tcVaradero(p) {
  return cubaBase({ ...p, usdTraslados: P.cuba.trasladosVaraderoUsd })
}

export function tcHabana(p) {
  return cubaBase({ ...p, usdTraslados: P.cuba.trasladosHabanaUsd })
}

export function tcVaraderoHabana({ vuelos, hotelHav, hotelVar, pax, trm, inicio, fin }) {
  return cubaBase({
    vuelos,
    hotel: hotelHav + hotelVar,
    pax, trm, inicio, fin,
    usdTraslados: P.cuba.trasladosCombinadoUsd
  })
}

// ---------------------------------------------------------------------
// TC PANAMA  (hoja "TC PANAMA")
// Playa (B32):    traslados IF(pax<2,100,50)*pax*trm*1.19
// Ciudad (F27):   traslados IF(pax<2,56,28)*pax*trm*1.19
// Combinado (J26): hotelCiudad + hotelPlaya
//                 + traslados IF(pax<2,136,68)*pax*trm*1.19
// Todas: + 1.2*pax*(fin-ini+1)*trm*1.15 de asistencia.
// Nota Excel: recargo nocturno traslados 20 USD/trayecto/persona
// (6PM-6AM) SE AGREGA MANUAL — no está en la fórmula.
// ---------------------------------------------------------------------
function panamaBase({ vuelos, hotel, pax, trm, inicio, fin, usdSolo, usdGrupo }) {
  return conValidacionFechas(inicio, fin, d =>
    mround(
      vuelos * fee() +
      hotel +
      P.panama.asistenciaUsdDia * pax * (d + 1) * trm * fee() +
      (pax < 2 ? usdSolo : usdGrupo) * pax * trm * P.panama.ivaTraslados,
      redondeo()
    )
  )
}

export function tcPanamaPlaya(p) {
  return panamaBase({ ...p, usdSolo: P.panama.playaSoloUsd, usdGrupo: P.panama.playaGrupoUsd })
}

export function tcPanamaCiudad(p) {
  return panamaBase({ ...p, usdSolo: P.panama.ciudadSoloUsd, usdGrupo: P.panama.ciudadGrupoUsd })
}

export function tcPanamaCombinado({ vuelos, hotelCiudad, hotelPlaya, pax, trm, inicio, fin }) {
  return panamaBase({
    vuelos,
    hotel: hotelCiudad + hotelPlaya,
    pax, trm, inicio, fin,
    usdSolo: P.panama.combinadoSoloUsd,
    usdGrupo: P.panama.combinadoGrupoUsd
  })
}

// ---------------------------------------------------------------------
// OTROS DESTINOS EN USD  (hoja "OTROS", celda B20)
// =MROUND(vuelos*1.15/trm + hotelUSD*1.06 + 1.4*pax*(fin-ini+1)*1.15, 1)
// Resultado en USD.
// ---------------------------------------------------------------------
export function tcOtrosUSD({ vuelos, hotelUSD, pax, trm, inicio, fin }) {
  return conValidacionFechas(inicio, fin, d =>
    mround(
      (vuelos * fee()) / trm +
      hotelUSD * P.otros.feeHotel +
      P.otros.asistenciaUsdDia * pax * (d + 1) * fee(),
      1
    )
  )
}

// ---------------------------------------------------------------------
// TURQUIA  (hoja "TURQUIA")
// Fechas: ida = inicioPlan - 1 ; regreso = ida + 10
// Terrestre (B13):
//   =IF(pax=1,699, IF(ISODD(pax), ((pax-1)/2*699+699), pax/2*699))
//     *trm*0.85 + nochesAdicionales/1.18
// Asistencia (B14): 1.4*pax*(regreso-ida+1)*trm
// Venta (B16): MROUND((vueloNeto + terrestre + asistencia)*1.2, 1000)
// Por persona (B17): venta/pax
// ---------------------------------------------------------------------
function tierParejas(pax, valorPareja, valorSencilla = valorPareja) {
  if (pax === 1) return valorSencilla
  if (isOdd(pax)) return ((pax - 1) / 2) * valorPareja + valorSencilla
  return (pax / 2) * valorPareja
}

export function turquia({ inicioPlan, pax, trm, vueloNeto, nochesAdicionales = 0 }) {
  const t = P.turquia
  const ida = sumarDias(inicioPlan, -t.diasAntesSalida)
  const regreso = sumarDias(ida, t.diasPlan)
  const duracion = diffDias(ida, regreso) + 1

  const terrestre =
    tierParejas(pax, t.tarifaParejaUsd) * trm * t.factorTrm +
    nochesAdicionales / t.ivaNochesAdicionales
  const asistencia = t.asistenciaUsdDia * pax * duracion * trm
  const total = mround((vueloNeto + terrestre + asistencia) * t.margen, redondeo())

  return {
    ida,
    regreso,
    terrestre,
    asistencia,
    total,
    porPersona: total / pax
  }
}

// ---------------------------------------------------------------------
// TAILANDIA  (hoja "TAILANDIA")
// Fechas: ida = inicioPlan - 2 ; interno = ida + 9 ; regreso = ida + 13
// Terrestre (B13):
//   =IF(pax=1,2409, IF(ISODD(pax), ((pax-1)/2*3329+2409), pax/2*3329))
//     *trm*0.88 + nochesAdicionales/1.18
// Venta (B16): MROUND((vueloNeto + terrestre)*1.2, 1000)
// Por persona (B17): venta/pax
// Niño con 2 adultos (B27): porPersona - (2409-1429)*trm*0.88
// ---------------------------------------------------------------------
export function tailandia({ inicioPlan, pax, trm, vueloNeto, nochesAdicionales = 0 }) {
  const t = P.tailandia
  const ida = sumarDias(inicioPlan, -t.diasAntesSalida)
  const interno = sumarDias(ida, t.diaVueloInterno)
  const regreso = sumarDias(ida, t.diaRegreso)

  const terrestre =
    tierParejas(pax, t.tarifaParejaUsd, t.tarifaSencillaUsd) * trm * t.factorTrm +
    nochesAdicionales / t.ivaNochesAdicionales
  const total = mround((vueloNeto + terrestre) * t.margen, redondeo())
  const porPersona = total / pax

  return {
    ida,
    interno,
    regreso,
    terrestre,
    total,
    porPersona,
    porPersonaNino: porPersona - (t.tarifaSencillaUsd - t.tarifaNinoUsd) * trm * t.factorTrm
  }
}

// ---------------------------------------------------------------------
// CRUCERO CTG  (hoja "CRUCERO CTG")
// Fechas: salida .. salida+7 (8 días)
// Naviera (A17): fecha < 2026-05-03 -> "SERENADE OF THE SEAS - ROYAL
// CARIBBEAN", si no -> "GRANDEUR OF THE SEAS - ROYAL CARIBBEAN"
// Precios de cabina: MROUND(valor, 1) — vienen digitados en USD.
// Etiqueta de cabina por pax (A19/A25).
// ---------------------------------------------------------------------
export function cruceroCtg({ fechaSalida, pax, cabinas }) {
  // cabinas = { interior: {sin, con}, exterior: {...}, balcon: {...}, suite: {...} }
  const c = P.crucero
  const regreso = sumarDias(fechaSalida, c.noches)

  const naviera =
    diffDias(fechaSalida, c.fechaCambioNaviera) > 0
      ? c.navieraAntes
      : c.navieraDespues

  const tipoCabina =
    pax < 2 ? "Sencilla" :
    pax < 3 ? "Doble" :
    pax < 4 ? "Triple" :
    pax < 5 ? "Cuadruple" : "Quintuple"

  const redondear = obj => ({
    sin: mround(obj?.sin ?? 0, 1),
    con: mround(obj?.con ?? 0, 1)
  })

  return {
    regreso,
    naviera,
    tipoCabina,
    precios: {
      interior: redondear(cabinas.interior),
      exterior: redondear(cabinas.exterior),
      balcon: redondear(cabinas.balcon),
      suite: redondear(cabinas.suite)
    }
  }
}

// ---------------------------------------------------------------------
// SAN ANDRES (hojas "SAN ANDRES 2026 TBAJA" y "SAN ANDRES 2025 TALTA")
//
// Solo desayuno (B31): MROUND(vuelos*1.15 + netoHotel, 1000)
//   (los netos vienen de Travel Compositor, se digitan totales)
//
// Todo incluido (B36): por cada habitación con tarifas {sgl,dbl,chd}:
//   IF(adultos=1,
//     IF(ninos=0, SGL,
//     IF(ninos=1, DBL*2, DBL*2+(ninos-1)*CHD)),
//   DBL*adultos + ninos*CHD)
// Suma de habitaciones * noches(fin-ini) * 0.87 * 1.18 + vuelos*1.15,
// todo MROUND a 1000.
//
// ⚠️ Nota: en el Excel original las habitaciones 7 en adelante tienen
// un typo (la condición usa $B$13 en vez de $B$14). Aquí se implementa
// la lógica CORRECTA (cada habitación evalúa sus propios adultos).
// ---------------------------------------------------------------------
export function costoHabitacionSanAndres(habitacion, tarifa) {
  const { adultos = 0, ninos = 0 } = habitacion
  const { sgl, dbl, chd } = tarifa

  if (adultos === 1) {
    if (ninos === 0) return sgl
    if (ninos === 1) return dbl * 2
    return dbl * 2 + (ninos - 1) * chd
  }
  return dbl * adultos + ninos * chd
}

export function sanAndresTodoIncluido({ vuelos, habitaciones, tarifa, inicio, fin }) {
  return conValidacionFechas(inicio, fin, noches => {
    const totalNoche = habitaciones.reduce(
      (acc, hab) => acc + costoHabitacionSanAndres(hab, tarifa),
      0
    )
    return mround(
      vuelos * fee() + totalNoche * noches * P.sanAndres.factorComision * P.sanAndres.iva,
      redondeo()
    )
  })
}

export function sanAndresSoloDesayuno({ vuelos, netoHotel, inicio, fin }) {
  return conValidacionFechas(inicio, fin, () =>
    mround(vuelos * fee() + netoHotel, redondeo())
  )
}

/**
 * Validación de acomodación (columna D del Excel):
 * =IF((adultos+ninos)>5,"VALIDAR LA ACOMODACION...",
 *  IF(adultos=0,"VALIDAR LA CANTIDAD DE ADULTOS","OK"))
 */
export function validarHabitacionSanAndres({ adultos = 0, ninos = 0 }) {
  if (adultos + ninos > 5) return "VALIDAR LA ACOMODACION DE ESTA HABITACION"
  if (adultos === 0) return "VALIDAR LA CANTIDAD DE ADULTOS"
  return "OK"
}

// ---------------------------------------------------------------------
// SANTA MARTA / CARTAGENA TEMPORADA BAJA
// (hojas "SANTA MARTA BAJA 2025" y "CARTAGENA BAJA 2025")
//
// Tarifa por acomodación (tabla lateral):
//   TOTAL = BASE_COMISIONABLE + IMPUESTOS - BASE_COMISIONABLE*0.14
//
// Solo desayuno (B37): MROUND(vuelos*1.15 + netoHotel*1.19, 1000)
// Plan con alimentación —desayuno+cena o todo incluido— (B42):
//   tarifaNoche = tarifaAcomodacion * pax   (en el Excel: =+T17*pax)
//   MROUND(vuelos*1.15 + tarifaNoche*(fin-ini)*1.18, 1000)
// ---------------------------------------------------------------------
export function tarifaTotalCosta({ baseComisionable, impuestos }) {
  return baseComisionable + impuestos - baseComisionable * P.costa.descuentoBase
}

export function costaSoloDesayuno({ vuelos, netoHotel, inicio, fin }) {
  return conValidacionFechas(inicio, fin, () =>
    mround(vuelos * fee() + netoHotel * P.costa.ivaSoloDesayuno, redondeo())
  )
}

export function costaConAlimentacion({ vuelos, tarifaAcomodacion, pax, inicio, fin }) {
  return conValidacionFechas(inicio, fin, noches =>
    mround(
      vuelos * fee() + tarifaAcomodacion * pax * noches * P.costa.ivaAlimentacion,
      redondeo()
    )
  )
}

// ---------------------------------------------------------------------
// GRUPOS TRAVEL  (hoja "GRUPOS TRAVEL", celda B31)
// Aplica igual para TI / desayuno+cena / solo desayuno:
// =MROUND(vuelos*1.15 + netoHotel*1.18, 1000)
// ---------------------------------------------------------------------
export function gruposTravel({ vuelos, netoHotel, inicio, fin }) {
  return conValidacionFechas(inicio, fin, () =>
    mround(vuelos * fee() + netoHotel * P.gruposTravel.factorHotel, redondeo())
  )
}
